import { debounce } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  getPlaceAutocompleteSuggestions,
  getPlaceFormattedAddress,
  type PlaceAutocompleteSuggestion,
} from './apiHelpers';

const MINIMUM_SEARCH_CHARS = 3;
const DEBOUNCE_SEARCH_MS = 250;

const newSessionToken = (): string => crypto.randomUUID();

export const useGooglePlaceAutocomplete = (restaurantId: string) => {
  const [suggestions, setSuggestions] = useState<PlaceAutocompleteSuggestion[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const sessionTokenRef = useRef(newSessionToken());
  // Incremented on every search/clear so responses from superseded requests are ignored.
  const latestRequestIdRef = useRef(0);

  const debouncedSearch = useMemo(
    () =>
      debounce(async (query: string): Promise<void> => {
        const requestId = ++latestRequestIdRef.current;
        try {
          const results = await getPlaceAutocompleteSuggestions({
            restaurantId,
            input: query,
            sessionToken: sessionTokenRef.current,
          });
          if (requestId === latestRequestIdRef.current) setSuggestions(results);
        } catch {
          if (requestId === latestRequestIdRef.current) setSuggestions([]);
        } finally {
          if (requestId === latestRequestIdRef.current) setLoading(false);
        }
      }, DEBOUNCE_SEARCH_MS),
    [restaurantId],
  );

  useEffect(() => () => debouncedSearch.cancel(), [debouncedSearch]);

  const clearSuggestions = () => {
    debouncedSearch.cancel();
    latestRequestIdRef.current += 1;
    setSuggestions([]);
    setLoading(false);
  };

  const search = (query: string) => {
    if (query.trim().length < MINIMUM_SEARCH_CHARS) {
      clearSuggestions();
      return;
    }
    setLoading(true);
    void debouncedSearch(query);
  };

  /** Resolves a selected suggestion to its full formatted address, then starts a fresh billing session. */
  const resolvePlace = async (
    suggestion: PlaceAutocompleteSuggestion,
  ): Promise<string | null> => {
    try {
      const formattedAddress = await getPlaceFormattedAddress({
        restaurantId,
        placeId: suggestion.placeId,
        sessionToken: sessionTokenRef.current,
      });
      return formattedAddress ?? suggestion.address;
    } catch {
      return null;
    } finally {
      sessionTokenRef.current = newSessionToken();
    }
  };

  return {
    suggestions,
    loading,
    search,
    clearSuggestions,
    resolvePlace,
  };
};
